import {
  EVENT_NAME_VARIABLE,
  IEventSurveyConfig,
  IEventSurveyPayload,
  IEventSurveyQuestion,
  TEventSurveyQuestionType
} from '../../../../services/Event/surveyTypes'

/** Pergunta em edição. `clientKey` é a chave do React, que não pode ser o id: a pergunta nova não tem. */
export interface IEventSurveyQuestionDraft {
  clientKey: string
  id: number | null
  type: TEventSurveyQuestionType
  statement: string
  short_label: string
  min_label: string
  max_label: string
  is_required: boolean
  is_nps: boolean
}

export interface IEventSurveyDraft {
  is_active: boolean
  questions: IEventSurveyQuestionDraft[]
}

export const QUESTION_TYPE_LABELS: Record<TEventSurveyQuestionType, string> = {
  scale: 'Nota de 0 a 10',
  text: 'Texto livre'
}

/** Limites espelhados das regras da API, para o campo travar antes de o envio ser recusado. */
export const MAX_QUESTIONS = 20
export const STATEMENT_MAX_LENGTH = 1000
export const SHORT_LABEL_MAX_LENGTH = 60
export const SCALE_LABEL_MAX_LENGTH = 60

const newClientKey = () => `question_${Date.now()}_${Math.floor(Math.random() * 100000)}`

export const toDraftQuestion = (question: IEventSurveyQuestion): IEventSurveyQuestionDraft => ({
  clientKey: question.id ? `question_${question.id}` : newClientKey(),
  id: question.id,
  type: question.type,
  statement: question.statement,
  short_label: question.short_label,
  min_label: question.min_label ?? '',
  max_label: question.max_label ?? '',
  is_required: question.is_required,
  is_nps: question.is_nps
})

/**
 * Sem pesquisa salva, a pesquisa nasce ativa: quem abre o modal para configurar quer que ela vá
 * no próximo envio de certificados — e o #LINK_PESQUISA do e-mail exige pesquisa ativa.
 */
export const toDraft = (config: IEventSurveyConfig): IEventSurveyDraft => ({
  is_active: config.survey?.is_active ?? true,
  questions: [...config.questions].sort((a, b) => a.position - b.position).map(toDraftQuestion)
})

export const newQuestion = (type: TEventSurveyQuestionType): IEventSurveyQuestionDraft => ({
  clientKey: newClientKey(),
  id: null,
  type,
  statement: '',
  short_label: '',
  min_label: '',
  max_label: '',
  is_required: type === 'scale',
  is_nps: false
})

const trimOrNull = (value: string) => value.trim() || null

export const toPayload = (draft: IEventSurveyDraft): IEventSurveyPayload => ({
  is_active: draft.is_active,
  questions: draft.questions.map((question) => {
    const isScale = question.type === 'scale'

    return {
      id: question.id,
      type: question.type,
      statement: question.statement.trim(),
      short_label: question.short_label.trim(),
      // Pergunta de texto não tem pontas de escala: os rótulos que ficaram de quando ela era de
      // nota não vão para a API.
      min_label: isScale ? trimOrNull(question.min_label) : null,
      max_label: isScale ? trimOrNull(question.max_label) : null,
      is_required: question.is_required,
      is_nps: isScale && question.is_nps
    }
  })
})

/** O que o participante lê: o site troca #NOME_EVENTO pelo nome do evento, e a prévia faz o mesmo. */
export const replaceEventName = (text: string, eventName: string) => text.split(EVENT_NAME_VARIABLE).join(eventName)

/** A participante fictícia de todas as prévias (página do site e exemplos de e-mail). */
export const SAMPLE_RECIPIENT_NAME = 'Ana'

/**
 * Exemplo de um e-mail com as variáveis trocadas, como a API troca no envio — o mesmo para o
 * e-mail dos certificados e para o envio da pesquisa.
 *
 * `exampleUrl` vem da API e é sintético (`.../pesquisa-evento/SEU-LINK-INDIVIDUAL`): o link de
 * verdade é um por participante e nunca aparece numa prévia.
 */
export const renderEmailSample = (text: string, eventName: string, exampleUrl?: string | null) =>
  replaceEventName(text, eventName)
    .split('#PRIMEIRO_NOME')
    .join(SAMPLE_RECIPIENT_NAME)
    .split('#LINK_PESQUISA')
    .join(exampleUrl ?? '[link individual da pesquisa]')

/**
 * Validação local, nas mesmas chaves que a API devolve (`questions.0.statement`), para que um
 * erro daqui e um de lá apareçam no mesmo lugar. A API continua sendo a regra: isto só poupa a
 * viagem nos casos óbvios.
 */
export const validateDraft = (draft: IEventSurveyDraft): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  const add = (key: string, message: string) => {
    errors[key] = [...(errors[key] ?? []), message]
  }

  if (draft.questions.length === 0) add('questions', 'A pesquisa precisa ter ao menos uma pergunta.')

  const seenLabels = new Map<string, number>()

  draft.questions.forEach((question, index) => {
    if (!question.statement.trim()) add(`questions.${index}.statement`, 'Informe o enunciado da pergunta.')

    const label = question.short_label.trim().toLocaleLowerCase('pt-BR')

    if (!label) {
      add(`questions.${index}.short_label`, 'Informe o rótulo curto da pergunta.')
    } else if (seenLabels.has(label)) {
      add(`questions.${index}.short_label`, 'Já existe outra pergunta com este rótulo curto.')
    } else {
      seenLabels.set(label, index)
    }
  })

  const npsCount = draft.questions.filter((question) => question.type === 'scale' && question.is_nps).length
  if (npsCount !== 1) add('questions', 'A pesquisa precisa ter exatamente uma pergunta de NPS (nota de 0 a 10).')

  return errors
}
